import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { distinctUntilChanged, distinctUntilKeyChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Account, Branch } from '../models/account';
import { CourierRepo } from '../repositories/courier.repo';
import { AuthAction } from '../authentication/auth-action';
import { AuthService } from '../authentication/auth.service';
import { Vehicle } from '../models/vehicle';
import { distinctUntilChangedObj } from '../operators/distinct-until-changed-obj';

@Injectable({
  providedIn: 'root'
})
export class AccountService {

  private readonly accountBS$ = new BehaviorSubject<Account | null>(null);
  account$: Observable<Account> = this.accountBS$.asObservable()
    .pipe(
      filter((account): account is Account => !!account),
      distinctUntilChangedObj()
    );

  constructor(
    private courierRepo: CourierRepo,
    private authService: AuthService,
  ) {
    this.authService.onAuthChanged.subscribe((authAction) => {
      if (authAction === AuthAction.Logout) {
        this.accountBS$.next(null);
      }
    })
  }

  getName(): Observable<string> {
    return this.account$
      .pipe(
        map(account => account.name),
        distinctUntilChanged(),
      );
  };

  getBranch(): Observable<Branch> {
    return this.account$
      .pipe(
        map(account => account.branch),
        distinctUntilKeyChanged('id'),
      );
  };

  getSelectedVehicle(): Observable<Vehicle> {
    return this.account$
      .pipe(
        map(account => account.currentSelectedVehicle),
        distinctUntilKeyChanged('id'),
      );
  };

  isAccountActive(): Observable<boolean> {
    return this.account$
      .pipe(
        map(account => account.active),
        distinctUntilChanged(),
      );
  }

  fetchAccount(): Observable<Account> {
    return this.courierRepo.getAccount()
      .pipe(tap((account) => {
        this.accountBS$.next(account);
      }));
  }

  updateVehicleInUse(vehicleId: number): Observable<unknown> {
    return this.account$
      .pipe(
        take(1),
        switchMap((account) =>
          this.courierRepo.updateAccount({
            name: account.name,
            email: account.email,
            telephoneNumber: account.telephoneNumber,
            selectedVehicleId: vehicleId
          })
        )
      )
  }

  setReadyForDelivery(ready: boolean): Observable<unknown> {
    const apiCall = ready ? this.courierRepo.postReadyForDelivery() : of(null);
    return apiCall.pipe(tap(() => {
      this.accountBS$.next({ ...this.accountBS$.value, ...{ readyForDelivery: ready } } as Account);
    }));
  }
}